import { useUnitData } from "../../database";
import { BarChart } from "./BarChart";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import "./Progress.css";
import { FIREBASE_AUTH } from "../../FirebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../constants";
import { motion, AnimatePresence } from "motion/react";

Chart.register(CategoryScale);

const games_mapping = {
  hideandseek: "Hide and Seek",
  jumprope: "Jump Rope",
  redlightgreenlight: "Red Light Green Light",
  tug: "Tug of War",
  jegi: "Jegi",
  marble: "Marble Game",
  mingle: "Mingle",
  flyingstone: "Flying Stone",
  sugar: "Sugar Honeycombs",
  gonggi: "Gong Gi",
};

const textbookOptions = [
  { value: "GT_textbook", label: "Old Textbook" },
  { value: "ttmik", label: "Talk To Me In Korean" },
];

function getAccuracy(correct, total) {
  return total > 0 ? Math.round((correct / total) * 100) : 0;
}

function getAccuracyColor(acc, attempted) {
  if (!attempted) return "rgba(224, 224, 224, 0.85)";
  if (acc >= 70) return "rgba(76, 175, 80, 0.85)";
  if (acc >= 40) return "rgba(255, 193, 7, 0.85)";
  return "rgba(244, 67, 54, 0.85)";
}

function processData(data, unitCount) {
  const n = unitCount;
  const processedData = {};

  for (const game in games_mapping) {
    processedData[game] = {
      attempted: new Array(n).fill(0),
      number_correct: new Array(n).fill(0),
      accuracy: new Array(n).fill(0),
    };
  }

  processedData.total_attempted = 0;
  processedData.total_correct = 0;

  data.forEach((item) => {
    if (!processedData[item.game]) return;
    if (item.unit < 1 || item.unit > n) return;
    processedData[item.game].attempted[item.unit - 1] = item.attempted;
    processedData[item.game].number_correct[item.unit - 1] = item.number_correct;
    processedData[item.game].accuracy[item.unit - 1] = getAccuracy(
      item.number_correct,
      item.attempted
    );
    processedData.total_attempted += item.attempted;
    processedData.total_correct += item.number_correct;
  });

  processedData.total_accuracy = getAccuracy(
    processedData.total_correct,
    processedData.total_attempted
  );

  processedData.unit_stats = [];
  for (let i = 0; i < n; i++) {
    let unitCorrect = 0, unitAttempted = 0, gamesMastered = 0, gamesPlayed = 0;
    for (const game in games_mapping) {
      unitCorrect += processedData[game].number_correct[i];
      unitAttempted += processedData[game].attempted[i];
      if (processedData[game].attempted[i] > 0) gamesPlayed++;
      if (processedData[game].accuracy[i] >= 70) gamesMastered++;
    }
    processedData.unit_stats.push({
      totalCorrect: unitCorrect,
      accuracy: getAccuracy(unitCorrect, unitAttempted),
      gamesMastered,
      gamesPlayed,
    });
  }

  return processedData;
}

function getAllUnitsChartData(data) {
  const labels = [];
  const accuracies = [];
  const colors = [];
  const borderColors = [];
  const attempts = [];

  for (let i = 0; i < data.unit_stats.length; i++) {
    labels.push(`Unit ${i + 1}`);
    const { accuracy, gamesPlayed, totalCorrect } = data.unit_stats[i];
    let totalAttempted = 0;
    for (const game in games_mapping) {
      totalAttempted += data[game].attempted[i];
    }
    accuracies.push(accuracy);
    attempts.push({ correct: totalCorrect, total: totalAttempted });
    const color = getAccuracyColor(accuracy, gamesPlayed);
    colors.push(color);
    if (gamesPlayed && accuracy >= 70) borderColors.push("rgba(76, 175, 80, 1)");
    else if (gamesPlayed && accuracy < 40) borderColors.push("rgba(244, 67, 54, 1)");
    else borderColors.push("transparent");
  }

  return {
    labels,
    datasets: [{
      label: "Accuracy (%)",
      data: accuracies,
      backgroundColor: colors,
      borderColor: borderColors,
      borderWidth: borderColors.map(c => c === "transparent" ? 0 : 2),
      borderRadius: 6,
      hoverBackgroundColor: colors.map(c => c.replace("0.85", "1")),
    }],
    _attempts: attempts,
  };
}

function getUnitBreakdownChartData(unitNum, data) {
  const labels = Object.values(games_mapping);
  const accuracies = [];
  const colors = [];
  const borderColors = [];
  const attempts = [];

  for (const game in games_mapping) {
    const acc = data[game].accuracy[unitNum - 1];
    const attempted = data[game].attempted[unitNum - 1];
    const correct = data[game].number_correct[unitNum - 1];
    accuracies.push(acc);
    attempts.push({ correct, total: attempted });
    const color = getAccuracyColor(acc, attempted);
    colors.push(color);
    if (attempted && acc >= 70) borderColors.push("rgba(76, 175, 80, 1)");
    else if (attempted && acc < 40) borderColors.push("rgba(244, 67, 54, 1)");
    else borderColors.push("transparent");
  }

  return {
    labels,
    datasets: [{
      label: `Unit ${unitNum} — Accuracy by Game (%)`,
      data: accuracies,
      backgroundColor: colors,
      borderColor: borderColors,
      borderWidth: borderColors.map(c => c === "transparent" ? 0 : 2),
      borderRadius: 6,
      hoverBackgroundColor: colors.map(c => c.replace("0.85", "1")),
    }],
    _attempts: attempts,
  };
}

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.45, ease: "easeOut" },
  }),
};

function Progress() {
  const [user, setUser] = useState(null);
  const [rawProgressData, setRawProgressData] = useState([]);
  const [selectedTextbook, setSelectedTextbook] = useState("GT_textbook");
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    data: unitData,
    loading: unitDataLoading,
    error: unitDataError,
  } = useUnitData(selectedTextbook);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (u) => {
      setUser(u);
      if (!u) {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    setSelectedUnit(null);
  }, [selectedTextbook]);

  useEffect(() => {
    if (!user) return;

    async function fetchData() {
      setLoading(true);
      try {
        const progressRes = await fetch(`${API_BASE_URL}/get_progress?user=${user.email}`);
        if (!progressRes.ok)
          throw new Error(`HTTP error! status: ${progressRes.status}`);
        setRawProgressData(await progressRes.json());
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [user]);

  const processedData = useMemo(() => {
    if (!unitData?.length) return null;

    const textbookProgress = rawProgressData.filter(
      (item) => item.textbook === selectedTextbook
    );

    return processData(textbookProgress, unitData.length);
  }, [rawProgressData, selectedTextbook, unitData]);

  const shouldShowLoadingScreen = loading || (!unitData?.length && unitDataLoading);

  if (shouldShowLoadingScreen) {
    return (
      <div className="progress-page">
        <div className="loading-screen">
          <h2>Loading progress...</h2>
          <h2>If you haven't signed in, please sign in to save and view progress</h2>
        </div>
      </div>
    );
  }

  if (unitDataError) {
    return (
      <div className="progress-page">
        <div className="loading-screen">
          <h2>Unable to load unit data.</h2>
        </div>
      </div>
    );
  }

  if (!processedData) return null;

  const totalGamesMastered = processedData.unit_stats.reduce((sum, u) => sum + u.gamesMastered, 0);

  const unitStats = selectedUnit !== null ? processedData.unit_stats[selectedUnit - 1] : null;
  const displayCorrect = unitStats ? unitStats.totalCorrect : processedData.total_correct;
  const displayAccuracy = unitStats ? unitStats.accuracy : processedData.total_accuracy;
  const displayMastered = unitStats
    ? `${unitStats.gamesMastered}/${Object.keys(games_mapping).length}`
    : `${totalGamesMastered}/${unitData.length * Object.keys(games_mapping).length}`;

  const chartData =
    selectedUnit === null
      ? getAllUnitsChartData(processedData)
      : getUnitBreakdownChartData(selectedUnit, processedData);

  return (
    <div className="progress-page">
      <div className="progress-container">
        <motion.div
          className="left-progress-section"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Stat cards */}
          <div className="stats-row">
            {[
              { value: displayCorrect, label: "Correct Answers", color: "green" },
              { value: `${displayAccuracy}%`, label: selectedUnit ? `Unit ${selectedUnit} Accuracy` : "Overall Accuracy", color: "yellow" },
              { value: displayMastered, label: "Games Mastered", color: "orange" },
            ].map((card, i) => (
              <motion.div
                className="stat-card"
                key={card.label}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
              >
                <span className={`stat-number ${card.color}`}>{card.value}</span>
                <span className="stat-label">{card.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Chart controls */}
          <div className="chart-header">
            <div className="chart-control-group">
              <label htmlFor="textbook-select" className="chart-label">Textbook:</label>
              <select
                id="textbook-select"
                className="unit-dropdown"
                value={selectedTextbook}
                onChange={(e) => setSelectedTextbook(e.target.value)}
              >
                {textbookOptions.map((textbook) => (
                  <option key={textbook.value} value={textbook.value}>
                    {textbook.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="chart-control-group chart-control-group-grow">
              <label htmlFor="unit-select" className="chart-label">View:</label>
              <select
                id="unit-select"
                className="unit-dropdown"
                value={selectedUnit === null ? "" : selectedUnit}
                onChange={(e) =>
                  setSelectedUnit(e.target.value === "" ? null : parseInt(e.target.value, 10))
                }
              >
                <option value="">All Units — Overall Accuracy</option>
                {unitData.map((unit) => (
                  <option key={unit.unit} value={unit.unit}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Color legend */}
          <div className="chart-legend">
            <span className="legend-item">
              <span className="legend-dot" style={{ background: "#4CAF50" }} />≥70%
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: "#FFC107" }} />40-69%
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: "#F44336" }} />&lt;40%
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: "#E0E0E0" }} />Not played
            </span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              className="chart-progress-section"
              key={selectedUnit === null ? "all" : `unit-${selectedUnit}`}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.3 }}
            >
              <h2 id="yaxis">Accuracy (%)</h2>
              <BarChart chartData={chartData} />
              <h2 id="xaxis">{selectedUnit === null ? "Units" : "Games"}</h2>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

export default Progress;
